import * as v2 from '@/core/services/api/v2';

const state = {
  fishCaught: [],
  fishingInProgress: false,
  loadingProgress: 0,
  currentFish: null,
  experience: 0,
  level: 1,
  selectedBait: null,
  currentMap: 'pier',
  availableMaps: [],
  allFishes: {},
  currentMapDetails: null,
  maps: {
    'pier': { id: 'pier', name: 'Píer' },
    'lake': { id: 'lake', name: 'Lago' },
    'river': { id: 'river', name: 'Rio' },
    'beach': { id: 'beach', name: 'Praia' },
    'deepSea': { id: 'deepSea', name: 'Mar Aberto' }
  }
};

const mutations = {
  START_FISHING(state) {
    state.fishingInProgress = true;
    state.loadingProgress = 0;
  },
  UPDATE_LOADING_PROGRESS(state, progress) {
    state.loadingProgress = progress;
  },
  CATCH_FISH(state, fish) {
    state.fishCaught.push(fish);
    state.currentFish = fish;
    state.experience = fish.playerExperience || 0;
    state.level = fish.playerLevel || 1;
    state.maxExperience = fish.maxExperience || state.maxExperience;
  },
  END_FISHING(state) {
    state.fishingInProgress = false;
    state.loadingProgress = 0;
    state.currentFish = null;
  },
  SELECT_BAIT(state, baitId) {
    state.selectedBait = baitId;
  },
  SET_CURRENT_MAP(state, mapId) {
    state.currentMap = mapId;
  },
  SET_AVAILABLE_MAPS(state, maps) {
    state.availableMaps = maps;
  },
  SET_ALL_FISHES(state, fishes) {
    state.allFishes = fishes;
  },
  SET_CURRENT_MAP_DETAILS(state, mapDetails) {
    state.currentMapDetails = mapDetails;
  },
  UPDATE_PLAYER_STATS(state, { experience, level, maxExperience }) {
    state.experience = experience;
    state.level = level;
    state.maxExperience = maxExperience;
  },
  SET_MAPS(state, maps) {
    state.maps = maps;
  }
};

const actions = {
  // As ações do contrato Strapi que viviam aqui — `startFishing`, `fetchMaps`,
  // `fetchMapDetails`, `useBait` e `calculateFishValue` — foram removidas:
  // nenhuma tinha chamador e o V2 resolve tudo isso dentro de `pescar` (delay,
  // mapa, isca e valor são decisão do servidor). Mapas vêm da store `maps` e
  // de `initializeMaps` abaixo.

  /**
   * Pescar, no backend V2.
   *
   * O corpo encolheu para `{ baitId }` — no máximo. Sumiram `baitPower`,
   * `currentMap`, `playerId` e `isHandFishing`, e cada um por um motivo:
   *
   * - `baitPower` era o poder da isca **dito pelo cliente**. Agora o servidor
   *   olha a isca no inventário e usa o poder do catálogo.
   * - `currentMap` era o mapa dito pelo cliente. Agora vale
   *   `players.current_map_id`, que só muda por rota de troca de mapa.
   * - `playerId` foi para a URL, onde o `OwnershipGuard` confere que é o dono.
   * - `isHandFishing` deixou de ser uma bandeira: pescar sem isca **é** pescar
   *   de mão. Uma bandeira que o cliente escolhe é uma bandeira que ele mente.
   *
   * A checagem de equipamento continua aqui porque é regra de interface — o
   * servidor não recusa quem pesca sem vara, só rende menos.
   */
  async fish({ rootState }, { isHandFishing = false } = {}) {
    try {
      const playerId = rootState.player.id;

      if (!playerId) {
        throw new Error('Player ID not available');
      }

      if (!isHandFishing) {
        const rod = rootState.player.equipment.rod;
        const bait = rootState.player.selectedBait;

        if (!rod || !bait) {
          throw new Error('Missing required equipment');
        }

        return await v2.pescar(playerId, bait.id);
      }

      return await v2.pescar(playerId);
    } catch (error) {
      console.error('Error fishing:', error);
      return {
        success: false,
        status: 'ERROR',
        message: 'Something went wrong while fishing'
      };
    }
  },

  /**
   * O bestiário inteiro, agrupado por mapa — `{ [mapId]: [peixes] }`.
   *
   * Quem precisa disso é o mercado: o getter `market/getFishPriceMultiplier`
   * descobre de qual mapa é um peixe varrendo esta lista. A rota do V2 é
   * pública e o adapter já devolve a forma que `SET_ALL_FISHES` guarda.
   */
  async fetchAllFishes({ commit }) {
    try {
      const porMapa = await v2.todosOsPeixes();
      commit('SET_ALL_FISHES', porMapa);
      return porMapa;
    } catch (error) {
      console.error(
        'Falha ao buscar o bestiário:',
        error.response?.data?.message || error.response?.data?.error?.message || error.message,
      );
      return {};
    }
  },

  /**
   * Os nomes dos mapas, para `getMapName`.
   *
   * Sai do catálogo do V2. O `state.maps` desta store nasce com cinco mapas
   * fixos em código (`pier`, `lake`, ...), herança de quando não havia catálogo;
   * a checagem abaixo nunca deixava buscar nada porque o objeto nunca está
   * vazio. Passou a comparar contra a lista embutida, para que a primeira carga
   * real substitua o chute.
   */
  async initializeMaps({ commit, state }) {
    // Só pula se já houver mapa vindo do servidor — chave numérica, e não os
    // nomes fixos com que o estado nasce.
    const jaVeioDoServidor = Object.keys(state.maps).some((chave) => !Number.isNaN(Number(chave)));
    if (jaVeioDoServidor) {
      return;
    }

    try {
      const itens = await v2.catalogo('map');
      const porId = {};
      itens.forEach((mapa) => {
        porId[mapa.id] = { id: mapa.id, name: mapa.name };
      });
      commit('SET_MAPS', porId);
    } catch (error) {
      console.error('Failed to load fishing maps:', error);
      // Keep default maps on error
    }
  }
};

const getters = {
  getCaughtFish: (state) => state.fishCaught,
  isFishingInProgress: (state) => state.fishingInProgress,
  getCurrentFish: (state) => state.currentFish,
  getExperience: (state) => state.experience,
  getLevel: (state) => state.level,
  getCurrentMap: (state) => state.currentMap,
  getAvailableMaps: (state) => state.availableMaps,
  getAllFishes: (state) => state.allFishes,
  getCurrentMapDetails: (state) => state.currentMapDetails,
  getFishesByMap: (state) => (mapId) => {
    return state.allFishes[mapId] || [];
  },
  getMapRequirements: (state) => (mapId) => {
    const map = state.availableMaps.find(m => m.id === mapId);
    return map ? map.required : null;
  },
  getMapName: (state) => (mapId) => {
    return state.maps[mapId]?.name || 'Desconhecido';
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};